import {
  type AccountDeletionCancelInput,
  type AccountDeletionRequestInput,
  type AccountDeletionStatusChallengeRequestInput,
  type AccountDeletionStatusChallengeVerifyInput,
  type AccountLinkStartInput,
  type AccountLinkUnlinkInput,
  type AdminAdmissionSettingsUpdateInput,
  type AdminCompanionSettingsUpdateInput,
  type AdminConstitutionActivateInput,
  type AdminConstitutionRetireInput,
  type AdminModerationActionInput,
  type AdminRoleChangeInput,
  accessPassOffersResponseSchema,
  accountDeletionCancelInputSchema,
  accountDeletionCancelResponseSchema,
  accountDeletionPreviewResponseSchema,
  accountDeletionRequestInputSchema,
  accountDeletionRequestResponseSchema,
  accountDeletionStatusChallengeRequestInputSchema,
  accountDeletionStatusChallengeRequestResponseSchema,
  accountDeletionStatusChallengeVerifyInputSchema,
  accountDeletionStatusChallengeVerifyResponseSchema,
  accountDeletionStatusResponseSchema,
  accountLinkStartInputSchema,
  accountLinkStartResponseSchema,
  accountLinkStatusQuerySchema,
  accountLinkStatusResponseSchema,
  accountLinkUnlinkInputSchema,
  accountLinkUnlinkResponseSchema,
  adminAdmissionSettingsSchema,
  adminAdmissionSettingsUpdateInputSchema,
  adminAuditListResponseSchema,
  adminBanUserResponseSchema,
  adminCompanionSettingsSchema,
  adminCompanionSettingsUpdateInputSchema,
  adminConstitutionActionResponseSchema,
  adminConstitutionActivateInputSchema,
  adminConstitutionCreateInputSchema,
  adminConstitutionCreateResponseSchema,
  adminConstitutionListResponseSchema,
  adminConstitutionRetireInputSchema,
  adminModerationActionInputSchema,
  adminRevokeMcpGrantsResponseSchema,
  adminRevokeWebSessionsResponseSchema,
  adminRoleChangeInputSchema,
  adminSetRoleResponseSchema,
  adminSupportActionInputSchema,
  adminSupportActionResponseSchema,
  adminSupportDetailResponseSchema,
  adminSupportListResponseSchema,
  adminUnbanUserResponseSchema,
  adminUserDetailResponseSchema,
  adminUserListResponseSchema,
  type BrowserCheckoutInput,
  billingStatusResponseSchema,
  browserCheckoutInputSchema,
  checkoutResponseSchema,
  creditPackOffersResponseSchema,
  linkedProviderListResponseSchema,
  paymentHistoryResponseSchema,
  refundSupportResponseSchema,
  type SupportChallengeRequestInput,
  type SupportChallengeVerifyInput,
  type SupportContactInput,
  supportChallengeRequestInputSchema,
  supportChallengeRequestResponseSchema,
  supportChallengeVerifyInputSchema,
  supportChallengeVerifyResponseSchema,
  supportContactInputSchema,
  supportContactResponseSchema,
  supportStatusResponseSchema,
  uppercaseUlidSchema,
} from "@tutorlite/types"
import { z } from "zod"

const configuredBase = import.meta.env.VITE_API_BASE_URL
const fallbackOrigin = window.location.hostname.startsWith("web-")
  ? `${window.location.protocol}//${window.location.hostname.replace(/^web-/, "api-")}`
  : window.location.origin
const configuredUrl = new URL(configuredBase ?? "/api/v1", fallbackOrigin)
export const apiOrigin = configuredUrl.origin
export const apiV1 = configuredUrl.pathname.startsWith("/api/v1")
  ? configuredUrl.toString().replace(/\/$/, "")
  : `${configuredUrl.origin}/api/v1`

const meSchema = z.strictObject({
  user: z.strictObject({ name: z.string(), email: z.string() }),
  owner: z.strictObject({
    status: z.enum(["active", "deactivated", "pending", "restricted"]),
    ageBand: z.enum(["adult", "minor", "unknown"]),
    locale: z.enum(["id", "en"]),
  }),
  enrollment: z.strictObject({
    status: z.enum(["active", "pending", "revoked", "suspended"]),
    method: z.enum(["approved_domain", "invite_only", "open"]),
  }),
  consent: z.strictObject({ required: z.boolean(), active: z.boolean() }),
  mode: z.enum(["compliance", "education"]),
  capabilities: z.strictObject({ progress: z.boolean(), recommendations: z.boolean() }),
  accountContext: z.strictObject({
    guardianOnly: z.boolean(),
    currentTermsAccepted: z.boolean(),
  }),
})

const masterySchema = z.strictObject({
  items: z.array(
    z.strictObject({
      topicId: z.string(),
      topicName: z.string(),
      collectionId: z.string(),
      collectionName: z.string(),
      locale: z.enum(["id", "en"]),
      difficulty: z.enum(["easy", "hard", "medium"]),
      score: z.number().min(0).max(100),
      state: z.enum(["learning", "mastered", "practicing", "unseen"]),
      evidenceCount: z.number().int().min(0),
      updatedAt: z.string().nullable(),
    }),
  ),
})
const clientsSchema = z.strictObject({
  clients: z.array(
    z.strictObject({
      grantId: z.string(),
      clientId: z.string(),
      scopes: z.array(z.string()),
      createdAt: z.union([z.number(), z.string()]),
      expiresAt: z.union([z.number(), z.string()]).optional(),
    }),
  ),
  cursor: z.string().optional(),
})
const onboardingSchema = z.strictObject({
  owner: z.strictObject({
    ownerId: z.string(),
    ownerStatus: z.string(),
    enrollmentId: z.string(),
    enrollmentMethod: z.string(),
    enrollmentStatus: z.string(),
    invitationConsumed: z.boolean(),
    ageBand: z.enum(["adult", "minor", "unknown"]),
    guardianRelationshipStatus: z.enum(["invited", "revoked", "verified"]).nullable(),
    guardianConsentStatus: z.enum(["active", "revoked", "superseded"]).nullable(),
    guardianOnlyEligible: z.boolean(),
    currentTermsAccepted: z.boolean(),
    nextStep: z.enum([
      "policy_review",
      "minor_guardian_email",
      "guardian_invitation_sent",
      "guardian_consent_pending",
      "complete",
    ]),
  }),
  terms: z.strictObject({
    version: z.string(),
    hash: z.string(),
    renderedLocale: z.enum(["id", "en"]),
    content: z.strictObject({
      title: z.string(),
      bodyFormat: z.literal("markdown"),
      bodyMarkdown: z.string(),
    }),
  }),
})
const pathSchema = z.strictObject({
  path: z
    .strictObject({
      id: z.string(),
      version: z.number(),
      constitutionVersion: z.string(),
      generatedAt: z.string(),
      items: z.array(
        z.strictObject({
          topicId: z.string(),
          topicName: z.string(),
          collectionName: z.string(),
          reasonCode: z.string(),
          priority: z.number(),
          nextAction: z.string(),
        }),
      ),
    })
    .nullable(),
})
const eventsSchema = z.strictObject({
  items: z.array(
    z.strictObject({
      id: z.string(),
      eventType: z.string(),
      entityType: z.string(),
      entityId: z.string(),
      topicId: z.string().nullable(),
      occurredAt: z.string(),
    }),
  ),
  nextCursor: z.string().optional(),
})
const analyticsSchema = z.strictObject({
  mastery: z.record(z.string(), z.number()),
  recentInteractionCount: z.number(),
  currentPeriodUsage: z.number(),
})
const guardianPolicyReviewSchema = z.strictObject({
  policy: z.strictObject({
    title: z.string(),
    bodyFormat: z.literal("markdown"),
    bodyMarkdown: z.string(),
  }),
  version: z.string(),
  hash: z.string(),
  renderedLocale: z.enum(["id", "en"]),
  purposes: z.strictObject({
    requiredForLearning: z.array(z.string()),
    optional: z.array(z.string()),
  }),
})
const guardianRelationshipsSchema = z.strictObject({
  relationships: z.array(
    z.strictObject({
      relationshipId: z.string(),
      role: z.enum(["guardian", "learner"]),
      status: z.enum(["invited", "revoked", "verified"]),
      consentStatus: z.enum(["active", "revoked", "superseded"]).nullable(),
      policyVersion: z.string().nullable(),
      purposes: z.array(z.string()),
    }),
  ),
})
const guardianSummarySchema = z.strictObject({
  periodStart: z.number(),
  periodEnd: z.number(),
  interactionCount: z.number(),
  masteryStates: z.array(z.unknown()),
  lastLearningAt: z.number().nullable(),
  nextTopic: z.string().nullable(),
})
const webSessionSchema = z
  .strictObject({
    session: z.strictObject({
      id: z.string().min(1),
      userId: z.string().min(1),
      createdAt: z.string().min(1),
      updatedAt: z.string().min(1),
      expiresAt: z.string().min(1),
      ipAddress: z.string().nullable().optional(),
      userAgent: z.string().nullable().optional(),
      impersonatedBy: z.string().nullable().optional(),
    }),
    user: z.strictObject({
      id: z.string().min(1),
      name: z.string().trim().min(1),
      email: z.email(),
      emailVerified: z.boolean(),
      image: z.string().nullable().optional(),
      createdAt: z.string().min(1),
      updatedAt: z.string().min(1),
      role: z.string().optional(),
      banned: z.boolean().nullable().optional(),
      banReason: z.string().nullable().optional(),
      banExpires: z.string().nullable().optional(),
    }),
  })
  .nullable()

export type LearnerMe = z.infer<typeof meSchema>
export type Mastery = z.infer<typeof masterySchema>
export type MasteryItem = Mastery["items"][number]
export type AuthorizedClient = z.infer<typeof clientsSchema>["clients"][number]
export type LinkedProviders = z.infer<typeof linkedProviderListResponseSchema>
export type AccountDeletionPreview = z.infer<typeof accountDeletionPreviewResponseSchema>
export type AccountDeletionStatus = z.infer<typeof accountDeletionStatusResponseSchema>
export type GuardianPolicyReview = z.infer<typeof guardianPolicyReviewSchema>
export type GuardianRelationship = z.infer<
  typeof guardianRelationshipsSchema
>["relationships"][number]
export type AccessPassOffer = z.infer<typeof accessPassOffersResponseSchema>["items"][number]
export type CreditPackOffer = z.infer<typeof creditPackOffersResponseSchema>["items"][number]
export type WebSession = z.infer<typeof webSessionSchema>
export type SupportStatus = z.infer<typeof supportStatusResponseSchema>
export type AdminSupportList = z.infer<typeof adminSupportListResponseSchema>
export type AdminCompanionSettings = z.infer<typeof adminCompanionSettingsSchema>
export type AdminAdmissionSettings = z.infer<typeof adminAdmissionSettingsSchema>
export type AdminConstitutionList = z.infer<typeof adminConstitutionListResponseSchema>
export type AdminAuditList = z.infer<typeof adminAuditListResponseSchema>
export type AdminUserList = z.infer<typeof adminUserListResponseSchema>
export type AdminUserDetail = z.infer<typeof adminUserDetailResponseSchema>
export type AccountExportProgress = { bytesReceived: number }

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code)
  }
}

async function request(path: string, init: RequestInit = {}) {
  const response = await fetch(`${apiV1}${path}`, { ...init, credentials: "include" })
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as
      | { error?: { code?: string } }
      | undefined
    throw new ApiError(response.status, body?.error?.code ?? "request_failed")
  }
  return response.json()
}

export async function getLearnerMe() {
  return meSchema.parse(await request("/learn/me"))
}

export async function getWebSession() {
  const response = await fetch(`${apiOrigin}/api/auth/get-session`, { credentials: "include" })
  if (!response.ok) {
    throw new ApiError(response.status, await responseErrorCode(response, "session_failed"))
  }
  return webSessionSchema.parse(await response.json())
}

export async function getMastery() {
  return masterySchema.parse(await request("/learn/mastery"))
}

export async function getLearningPath() {
  return pathSchema.parse(await request("/learn/path"))
}

export async function getLearningEvents(cursor?: string) {
  return eventsSchema.parse(
    await request(`/learn/events?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`),
  )
}

export async function getLearningAnalytics() {
  return analyticsSchema.parse(await request("/learn/analytics"))
}

export async function getBillingCatalog() {
  const [accessPasses, creditPacks] = await Promise.all([
    request("/billing/access-passes").then((value) => accessPassOffersResponseSchema.parse(value)),
    request("/billing/credit-packs").then((value) => creditPackOffersResponseSchema.parse(value)),
  ])
  return { accessPasses: accessPasses.items, creditPacks: creditPacks.items }
}

export async function getBillingStatus() {
  return billingStatusResponseSchema.parse(await request("/billing/status", { cache: "no-store" }))
}

export async function getBillingHistory(cursor?: string) {
  return paymentHistoryResponseSchema.parse(
    await request(
      `/billing/history?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  )
}

export async function createBillingCheckout(input: BrowserCheckoutInput) {
  const body = browserCheckoutInputSchema.parse(input)
  const response = await billingMutation("/billing/checkout", body)
  return checkoutResponseSchema.parse(response)
}

export async function requestBillingRefundSupport(paymentId: string) {
  const id = uppercaseUlidSchema.parse(paymentId)
  const response = await billingMutation(
    `/billing/history/${encodeURIComponent(id)}/refund-request`,
    {},
  )
  return refundSupportResponseSchema.parse(response)
}

export async function getSupportStatus() {
  return supportStatusResponseSchema.parse(await request("/support/status", { cache: "no-store" }))
}

export async function requestSupportChallenge(input: SupportChallengeRequestInput) {
  const body = supportChallengeRequestInputSchema.parse(input)
  return supportChallengeRequestResponseSchema.parse(
    await supportMutation("/support/challenge/request", body),
  )
}

export async function verifySupportChallenge(input: SupportChallengeVerifyInput) {
  const body = supportChallengeVerifyInputSchema.parse(input)
  return supportChallengeVerifyResponseSchema.parse(
    await supportMutation("/support/challenge/verify", body),
  )
}

export async function createSupportContact(input: SupportContactInput) {
  const body = supportContactInputSchema.parse(input)
  return supportContactResponseSchema.parse(await supportMutation("/support/contact", body))
}

export async function getAdminSupportRequests(cursor?: string) {
  return adminSupportListResponseSchema.parse(
    await request(
      `/admin/support/requests?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  )
}

export async function getAdminSupportRequest(id: string) {
  const requestId = uppercaseUlidSchema.parse(id)
  return adminSupportDetailResponseSchema.parse(
    await request(`/admin/support/requests/${encodeURIComponent(requestId)}`),
  )
}

export async function resolveAdminSupportRequest(id: string, reason: string) {
  const requestId = uppercaseUlidSchema.parse(id)
  const body = adminSupportActionInputSchema.parse({ reason })
  return adminSupportActionResponseSchema.parse(
    await supportMutation(`/admin/support/requests/${encodeURIComponent(requestId)}/resolve`, body),
  )
}

export async function closeAdminSupportRequest(id: string, reason: string) {
  const requestId = uppercaseUlidSchema.parse(id)
  const body = adminSupportActionInputSchema.parse({ reason })
  return adminSupportActionResponseSchema.parse(
    await supportMutation(`/admin/support/requests/${encodeURIComponent(requestId)}/close`, body),
  )
}

export async function markAdminSupportRequestSpam(id: string, reason: string) {
  const requestId = uppercaseUlidSchema.parse(id)
  const body = adminSupportActionInputSchema.parse({ reason })
  return adminSupportActionResponseSchema.parse(
    await supportMutation(`/admin/support/requests/${encodeURIComponent(requestId)}/spam`, body),
  )
}

export async function getAdminCompanionSettings() {
  return adminCompanionSettingsSchema.parse(await request("/admin/settings/companion"))
}

export async function updateAdminCompanionSettings(input: AdminCompanionSettingsUpdateInput) {
  const body = adminCompanionSettingsUpdateInputSchema.parse(input)
  return adminCompanionSettingsSchema.parse(
    await adminSettingsMutation("PATCH", "/admin/settings/companion", body),
  )
}

export async function getAdminAdmissionSettings() {
  return adminAdmissionSettingsSchema.parse(await request("/admin/settings/admission"))
}

export async function updateAdminAdmissionSettings(input: AdminAdmissionSettingsUpdateInput) {
  const body = adminAdmissionSettingsUpdateInputSchema.parse(input)
  return adminAdmissionSettingsSchema.parse(
    await adminSettingsMutation("PATCH", "/admin/settings/admission", body),
  )
}

export async function getAdminConstitutions() {
  return adminConstitutionListResponseSchema.parse(await request("/admin/settings/constitution"))
}

export async function createAdminConstitution(input: {
  version: string
  content: unknown
  reason: string
}) {
  const body = adminConstitutionCreateInputSchema.parse(input)
  return adminConstitutionCreateResponseSchema.parse(
    await adminSettingsMutation("POST", "/admin/settings/constitution", body),
  )
}

export async function activateAdminConstitution(id: string, input: AdminConstitutionActivateInput) {
  const body = adminConstitutionActivateInputSchema.parse(input)
  return adminConstitutionActionResponseSchema.parse(
    await adminSettingsMutation(
      "POST",
      `/admin/settings/constitution/${encodeURIComponent(id)}/activate`,
      body,
    ),
  )
}

export async function retireAdminConstitution(id: string, input: AdminConstitutionRetireInput) {
  const body = adminConstitutionRetireInputSchema.parse(input)
  return adminConstitutionActionResponseSchema.parse(
    await adminSettingsMutation(
      "POST",
      `/admin/settings/constitution/${encodeURIComponent(id)}/retire`,
      body,
    ),
  )
}

export async function getAdminAudit(cursor?: string) {
  return adminAuditListResponseSchema.parse(
    await request(`/admin/audit?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`),
  )
}

export async function getAdminUsers(input: { q?: string; cursor?: string } = {}) {
  const params = new URLSearchParams({ limit: "20" })
  if (input.q) params.set("q", input.q)
  if (input.cursor) params.set("cursor", input.cursor)
  return adminUserListResponseSchema.parse(await request(`/admin/users?${params}`))
}

export async function getAdminUserDetail(userId: string) {
  return adminUserDetailResponseSchema.parse(
    await request(`/admin/users/${encodeURIComponent(userId)}`),
  )
}

export async function banAdminUser(userId: string, input: AdminModerationActionInput) {
  const body = adminModerationActionInputSchema.parse(input)
  return adminBanUserResponseSchema.parse(
    await adminUsersMutation(`/admin/users/${encodeURIComponent(userId)}/ban`, body),
  )
}

export async function unbanAdminUser(userId: string, input: AdminModerationActionInput) {
  const body = adminModerationActionInputSchema.parse(input)
  return adminUnbanUserResponseSchema.parse(
    await adminUsersMutation(`/admin/users/${encodeURIComponent(userId)}/unban`, body),
  )
}

export async function revokeAdminUserWebSessions(
  userId: string,
  input: AdminModerationActionInput,
) {
  const body = adminModerationActionInputSchema.parse(input)
  return adminRevokeWebSessionsResponseSchema.parse(
    await adminUsersMutation(
      `/admin/users/${encodeURIComponent(userId)}/revoke-web-sessions`,
      body,
    ),
  )
}

export async function revokeAdminUserMcpGrants(userId: string, input: AdminModerationActionInput) {
  const body = adminModerationActionInputSchema.parse(input)
  return adminRevokeMcpGrantsResponseSchema.parse(
    await adminUsersMutation(`/admin/users/${encodeURIComponent(userId)}/revoke-mcp-grants`, body),
  )
}

export async function setAdminUserRole(userId: string, input: AdminRoleChangeInput) {
  const body = adminRoleChangeInputSchema.parse(input)
  return adminSetRoleResponseSchema.parse(
    await adminUsersMutation(`/admin/users/${encodeURIComponent(userId)}/set-role`, body),
  )
}

async function billingMutation(path: string, body: unknown) {
  const response = await fetch(`${apiV1}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-TutorLite-Request": "billing",
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new ApiError(response.status, await responseErrorCode(response, "billing_request_failed"))
  }
  return response.json()
}

async function supportMutation(path: string, body: unknown) {
  const response = await fetch(`${apiV1}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-TutorLite-Request": "support",
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new ApiError(response.status, await responseErrorCode(response, "support_request_failed"))
  }
  return response.json()
}

async function accountMutation(
  path: string,
  body: unknown,
  extraHeaders: Record<string, string> = {},
) {
  const response = await fetch(`${apiV1}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-TutorLite-Request": "account",
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new ApiError(response.status, await responseErrorCode(response, "account_request_failed"))
  }
  return response.json()
}

async function adminSettingsMutation(method: "PATCH" | "POST", path: string, body: unknown) {
  const response = await fetch(`${apiV1}${path}`, {
    method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-TutorLite-Request": "admin-settings",
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new ApiError(response.status, await responseErrorCode(response, "admin_settings_failed"))
  }
  return response.json()
}

async function adminUsersMutation(path: string, body: unknown) {
  const response = await fetch(`${apiV1}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-TutorLite-Request": "admin-users",
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new ApiError(response.status, await responseErrorCode(response, "admin_users_failed"))
  }
  return response.json()
}

export async function getAuthorizedClients(cursor?: string) {
  return clientsSchema.parse(
    await request(
      `/account/authorized-clients${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  )
}

export async function revokeAuthorizedClient(grantId: string) {
  const response = await fetch(
    `${apiV1}/account/authorized-clients/${encodeURIComponent(grantId)}/revoke`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    },
  )
  if (!response.ok) throw new ApiError(response.status, "revoke_failed")
}

export async function getLinkedProviders() {
  return linkedProviderListResponseSchema.parse(
    await request("/account/linked-providers", { cache: "no-store" }),
  )
}

export async function startAccountProviderLink(input: AccountLinkStartInput) {
  const body = accountLinkStartInputSchema.parse(input)
  return accountLinkStartResponseSchema.parse(
    await accountMutation("/account/linked-providers/start", body),
  )
}

export async function getAccountProviderLinkStatus(attemptId: string, error?: string | null) {
  const query = accountLinkStatusQuerySchema.parse({
    attemptId,
    ...(error ? { error } : {}),
  })
  const params = new URLSearchParams({ attemptId: query.attemptId })
  if (query.error) params.set("error", query.error)
  return accountLinkStatusResponseSchema.parse(
    await request(`/account/linked-providers/status?${params.toString()}`, { cache: "no-store" }),
  )
}

export async function unlinkAccountProvider(input: AccountLinkUnlinkInput) {
  const body = accountLinkUnlinkInputSchema.parse(input)
  return accountLinkUnlinkResponseSchema.parse(
    await accountMutation("/account/linked-providers/unlink", body),
  )
}

export async function getAccountDeletionPreview(locale: "id" | "en" = "id") {
  return accountDeletionPreviewResponseSchema.parse(
    await request(`/account/delete/preview?locale=${encodeURIComponent(locale)}`, {
      cache: "no-store",
    }),
  )
}

export async function requestAccountDeletion(input: AccountDeletionRequestInput) {
  const body = accountDeletionRequestInputSchema.parse(input)
  return accountDeletionRequestResponseSchema.parse(await accountMutation("/account/delete", body))
}

export async function getAccountDeletionStatus(proofToken?: string) {
  const init: RequestInit = { cache: "no-store" }
  if (proofToken) init.headers = { "X-TutorLite-Deletion-Proof": proofToken }
  return accountDeletionStatusResponseSchema.parse(await request("/account/delete/status", init))
}

export async function cancelAccountDeletion(
  input: AccountDeletionCancelInput,
  proofToken?: string,
) {
  const body = accountDeletionCancelInputSchema.parse(input)
  const headers: Record<string, string> = {}
  if (proofToken) headers["X-TutorLite-Deletion-Proof"] = proofToken
  return accountDeletionCancelResponseSchema.parse(
    await accountMutation("/account/delete/cancel", body, headers),
  )
}

export async function requestDeletionStatusChallenge(
  input: AccountDeletionStatusChallengeRequestInput,
) {
  const body = accountDeletionStatusChallengeRequestInputSchema.parse(input)
  return accountDeletionStatusChallengeRequestResponseSchema.parse(
    await supportMutation("/support/deletion-status/challenge/request", body),
  )
}

export async function verifyDeletionStatusChallenge(
  input: AccountDeletionStatusChallengeVerifyInput,
) {
  const body = accountDeletionStatusChallengeVerifyInputSchema.parse(input)
  return accountDeletionStatusChallengeVerifyResponseSchema.parse(
    await supportMutation("/support/deletion-status/challenge/verify", body),
  )
}

export async function exportAccountData(
  options: { signal?: AbortSignal; onProgress?: (progress: AccountExportProgress) => void } = {},
) {
  const response = await fetch(`${apiV1}/account/export`, {
    credentials: "include",
    cache: "no-store",
    ...(options.signal ? { signal: options.signal } : {}),
  })
  if (!response.ok) {
    throw new ApiError(response.status, await responseErrorCode(response, "account_export_failed"))
  }
  const chunks: ArrayBuffer[] = []
  let bytesReceived = 0
  const decoder = new TextDecoder()
  let text = ""
  if (response.body) {
    const reader = response.body.getReader()
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(
        value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as ArrayBuffer,
      )
      text += decoder.decode(value, { stream: true })
      bytesReceived += value.byteLength
      options.onProgress?.({ bytesReceived })
    }
    text += decoder.decode()
  } else {
    const value = new Uint8Array(await response.arrayBuffer())
    chunks.push(
      value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as ArrayBuffer,
    )
    bytesReceived = value.byteLength
    text = decoder.decode(value)
    options.onProgress?.({ bytesReceived })
  }
  if (!hasCompletedAccountExport(text)) throw new ApiError(502, "account_export_incomplete")
  return {
    blob: new Blob(chunks, {
      type: response.headers.get("Content-Type") ?? "application/x-ndjson; charset=utf-8",
    }),
    bytesReceived,
    filename: exportFilename(response.headers.get("Content-Disposition")),
  }
}

function hasCompletedAccountExport(value: string) {
  const lastLine = value.trim().split("\n").filter(Boolean).at(-1)
  if (!lastLine) return false
  try {
    const parsed = JSON.parse(lastLine) as { type?: unknown }
    return parsed.type === "export_end"
  } catch {
    return false
  }
}

export async function updateLocale(locale: "en" | "id") {
  const response = await fetch(`${apiV1}/account/locale`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ locale }),
  })
  if (!response.ok) throw new ApiError(response.status, "locale_failed")
}

function exportFilename(disposition: string | null) {
  const match = disposition?.match(/filename="?([^";]+)"?/i)
  return match?.[1] && /^[A-Za-z0-9._-]+$/.test(match[1])
    ? match[1]
    : "tutorlite-account-export.ndjson"
}

async function authPost(path: string, body: unknown) {
  const response = await fetch(`${apiOrigin}/api/auth${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  if (!response.ok) throw new ApiError(response.status, "auth_request_failed")
  return response.json()
}

export function registerAccount(input: { email: string; name: string; password: string }) {
  return authPost("/sign-up/email", input)
}

export function sendVerificationEmail(email: string, locale: "en" | "id") {
  return authPost("/send-verification-email", { email, locale })
}

export function consumeVerificationToken(token: string) {
  return authPost("/verify-email/consume", { token })
}

export function requestPasswordReset(email: string) {
  return authPost("/request-password-reset", {
    email,
    redirectTo: `${window.location.origin}/reset-password`,
  })
}

export function resetPassword(token: string, newPassword: string) {
  return authPost("/reset-password", { token, newPassword })
}

export async function getOnboarding(locale: "en" | "id") {
  return onboardingSchema.parse(await request(`/account/onboarding?locale=${locale}`))
}

type CompleteOnboardingInput =
  | {
      setup: "guardian_only"
      locale: "en" | "id"
      policyVersion: string
      policyHash: string
    }
  | {
      setup: "learner"
      ageBand: "adult" | "minor"
      locale: "en" | "id"
      policyVersion: string
      policyHash: string
    }

export async function completeOnboarding(input: CompleteOnboardingInput) {
  const response = await fetch(`${apiV1}/account/onboarding/complete`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as
      | { error?: { code?: string } }
      | undefined
    throw new ApiError(response.status, body?.error?.code ?? "onboarding_failed")
  }
}

export async function requestGuardianChallenge(guardianEmail: string, locale: "en" | "id") {
  const response = await fetch(`${apiV1}/account/guardian/challenge/request`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ guardianEmail, locale }),
  })
  if (!response.ok) throw new ApiError(response.status, "guardian_request_failed")
}

export async function verifyGuardianChallenge(input: {
  token: string
  locale: "en" | "id"
  purposes: string[]
  reviewedPolicyVersion: string
  reviewedPolicyHash: string
}) {
  const response = await fetch(`${apiV1}/account/guardian/challenge/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  })
  if (!response.ok)
    throw new ApiError(response.status, await responseErrorCode(response, "guardian_verify_failed"))
}

export async function reviewGuardianChallenge(token: string, locale: "en" | "id") {
  const response = await fetch(`${apiV1}/account/guardian/challenge/review`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, locale }),
  })
  if (!response.ok)
    throw new ApiError(response.status, await responseErrorCode(response, "guardian_review_failed"))
  return guardianPolicyReviewSchema.parse(await response.json())
}

export async function getGuardianRelationships() {
  return guardianRelationshipsSchema.parse(await request("/account/guardian"))
}

export async function getGuardianSummary(relationshipId: string) {
  return guardianSummarySchema.parse(
    await request(`/account/guardian/learners/${encodeURIComponent(relationshipId)}/summary`),
  )
}

export async function revokeGuardianConsent(relationshipId: string, purposes: string[]) {
  const response = await fetch(`${apiV1}/account/guardian/consent/revoke`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ relationshipId, purposes }),
  })
  if (!response.ok) throw new ApiError(response.status, "guardian_revoke_failed")
  return z
    .strictObject({ remainingPurposes: z.array(z.string()), revocationPending: z.boolean() })
    .parse(await response.json())
}

export async function signOut() {
  const response = await fetch(`${apiOrigin}/api/auth/sign-out`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  })
  if (!response.ok) throw new ApiError(response.status, "sign_out_failed")
}

async function responseErrorCode(response: Response, fallback: string) {
  const body = (await response.json().catch(() => undefined)) as
    | { error?: { code?: string } }
    | undefined
  return body?.error?.code ?? fallback
}
